import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationAPI, matchAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Bell, 
  Check, 
  Trash2, 
  Users, 
  Calendar,
  CheckCheck,
  ChevronRight,
  Swords,
  UserPlus,
  X,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null); // Track which invitation is being responded to

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await notificationAPI.getAll(100);
      setNotifications(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benachrichtigungen');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (notification) => {
    if (!notification.reference_id) {
      toast.error('Spiel-ID nicht gefunden');
      return;
    }
    
    setRespondingTo(`accept-${notification.id}`);
    try {
      await matchAPI.acceptInvitation(notification.reference_id);
      toast.success('Einladung angenommen! Du bist jetzt Teilnehmer.');
      // Mark notification as read and update its status visually
      await notificationAPI.markAsRead(notification.id);
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, read: true, invitation_responded: 'accepted' } 
          : n
      ));
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Annehmen der Einladung';
      toast.error(message);
    } finally {
      setRespondingTo(null);
    }
  };

  const handleDeclineInvitation = async (notification) => {
    if (!notification.reference_id) {
      toast.error('Spiel-ID nicht gefunden');
      return;
    }
    
    setRespondingTo(`decline-${notification.id}`);
    try {
      await matchAPI.declineInvitation(notification.reference_id);
      toast.success('Einladung abgelehnt.');
      // Mark notification as read and update its status visually
      await notificationAPI.markAsRead(notification.id);
      setNotifications(prev => prev.map(n => 
        n.id === notification.id 
          ? { ...n, read: true, invitation_responded: 'declined' } 
          : n
      ));
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Ablehnen der Einladung';
      toast.error(message);
    } finally {
      setRespondingTo(null);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      toast.error('Fehler beim Markieren als gelesen');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Alle als gelesen markiert');
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Benachrichtigung gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'match_application':
      case 'match_accepted':
      case 'match_rejected':
        return <Calendar className="w-6 h-6 text-tunis-blue" />;
      case 'match_invitation':
        return <UserPlus className="w-6 h-6 text-tunis-teal" />;
      case 'team_application':
      case 'team_accepted':
      case 'team_rejected':
        return <Users className="w-6 h-6 text-tunis-teal" />;
      case 'challenge_received':
      case 'challenge_accepted':
      case 'challenge_rejected':
        return <Swords className="w-6 h-6 text-amber-500" />;
      case 'result_reminder':
        return <Calendar className="w-6 h-6 text-orange-500" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'match_application':
        return { label: 'Spiel-Bewerbung', color: 'bg-blue-100 text-blue-700' };
      case 'match_accepted':
        return { label: 'Angenommen', color: 'bg-green-100 text-green-700' };
      case 'match_rejected':
        return { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' };
      case 'match_invitation':
        return { label: 'Spieleinladung', color: 'bg-teal-100 text-teal-700' };
      case 'team_application':
        return { label: 'Team-Bewerbung', color: 'bg-teal-100 text-teal-700' };
      case 'team_accepted':
        return { label: 'Angenommen', color: 'bg-green-100 text-green-700' };
      case 'team_rejected':
        return { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' };
      case 'challenge_received':
        return { label: 'Herausforderung', color: 'bg-amber-100 text-amber-700' };
      case 'challenge_accepted':
        return { label: 'Angenommen', color: 'bg-green-100 text-green-700' };
      case 'challenge_rejected':
        return { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' };
      case 'result_reminder':
        return { label: 'Erinnerung', color: 'bg-orange-100 text-orange-700' };
      default:
        return { label: 'Info', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const getLink = (notification) => {
    if (notification.reference_type === 'match' && notification.reference_id) {
      return `/matches/${notification.reference_id}`;
    }
    if (notification.reference_type === 'match_invitation' && notification.reference_id) {
      return `/matches/${notification.reference_id}`;
    }
    if (notification.reference_type === 'team' && notification.reference_id) {
      return `/teams/${notification.reference_id}`;
    }
    if (notification.reference_type === 'challenge') {
      return `/challenges`;
    }
    return null;
  };

  // Check if notification is an invitation that can be responded to
  const canRespondToInvitation = (notification) => {
    return notification.type === 'match_invitation' && 
           notification.reference_id && 
           !notification.invitation_responded;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-4 sm:py-8">
          <Skeleton className="h-8 sm:h-10 w-40 sm:w-48 mb-4 sm:mb-6" />
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 sm:h-24 mb-3 sm:mb-4" />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div>
            <h1 className="font-display font-bold text-xl sm:text-2xl text-tunis-blue">
              Benachrichtigungen
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} ungelesen` : 'Alle gelesen'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="gap-2 w-full sm:w-auto"
            >
              <CheckCheck className="w-4 h-4" />
              Alle gelesen
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 sm:p-12 text-center">
              <Bell className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-slate-300" />
              <h3 className="font-medium text-base sm:text-lg mb-2">Keine Benachrichtigungen</h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                Du hast noch keine Benachrichtigungen erhalten.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {notifications.map((notification) => {
              const typeInfo = getTypeLabel(notification.type);
              const link = getLink(notification);
              
              return (
                <Card 
                  key={notification.id} 
                  className={`border-slate-200 transition-all ${
                    !notification.read ? 'bg-tunis-teal/5 border-tunis-teal/20' : ''
                  }`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex gap-3 sm:gap-4">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          {getIcon(notification.type)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <h3 className={`font-medium text-sm sm:text-base ${!notification.read ? 'text-tunis-blue' : ''}`}>
                              {notification.title}
                            </h3>
                            <Badge className={`${typeInfo.color} text-[10px] sm:text-xs`}>
                              {typeInfo.label}
                            </Badge>
                            {/* Show response status for invitations */}
                            {notification.invitation_responded && (
                              <Badge className={`text-[10px] sm:text-xs ${
                                notification.invitation_responded === 'accepted' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {notification.invitation_responded === 'accepted' ? 'Angenommen' : 'Abgelehnt'}
                              </Badge>
                            )}
                          </div>
                          {!notification.read && (
                            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-tunis-teal rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm sm:text-base text-muted-foreground mb-2 line-clamp-2">
                          {notification.message}
                        </p>
                        
                        {/* Invitation Accept/Decline Buttons */}
                        {canRespondToInvitation(notification) && (
                          <div className="flex flex-col sm:flex-row gap-2 mb-3 p-2 sm:p-3 bg-slate-50 rounded-lg">
                            <Button
                              size="sm"
                              onClick={() => handleAcceptInvitation(notification)}
                              disabled={respondingTo !== null}
                              className="bg-green-600 hover:bg-green-700 text-white flex-1 text-xs sm:text-sm"
                            >
                              {respondingTo === `accept-${notification.id}` ? (
                                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              )}
                              Annehmen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeclineInvitation(notification)}
                              disabled={respondingTo !== null}
                              className="border-red-300 text-red-600 hover:bg-red-50 flex-1 text-xs sm:text-sm"
                            >
                              {respondingTo === `decline-${notification.id}` ? (
                                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                              ) : (
                                <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              )}
                              Ablehnen
                            </Button>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <span className="text-[10px] sm:text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(notification.created_at), { 
                              addSuffix: true, 
                              locale: de 
                            })}
                          </span>
                          <div className="flex items-center gap-1 sm:gap-2">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="text-tunis-teal hover:text-tunis-teal/80 text-xs sm:text-sm px-2 sm:px-3"
                              >
                                <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                <span className="hidden sm:inline">Gelesen</span>
                              </Button>
                            )}
                            {link && (
                              <Link to={link}>
                                <Button variant="ghost" size="sm" className="text-tunis-blue text-xs sm:text-sm px-2 sm:px-3">
                                  <span className="hidden sm:inline">Anzeigen</span>
                                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 sm:ml-1" />
                                </Button>
                              </Link>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notification.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2 sm:px-3"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
